// src/pages/Webinar.js
import React from "react";
import { Box } from "@chakra-ui/react";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WebinarHero from "../components/webinar/WebinarHero";
import WebinarLearning from "../components/webinar/WebinarLearning";
import WebinarRegistration from '../components/webinar/WebinarRegistration';
import WebinarSocialProof from "../components/webinar/WebinarSocialProof";
import WebinarNextSteps from "../components/webinar/WebinarNextSteps";
import WebinarFAQ from "../components/webinar/WebinarFAQ";
import WebinarTrust from "../components/webinar/WebinarTrust";

import { componentMessaging, coreMessaging } from "../data/messaging";
import { useFaq } from '../data/faq';
import {
  MdCheckCircle,
  MdLockOpen,
  MdUnsubscribe,
  MdEmail,
  MdVideoLibrary,
  MdDescription,
  MdEvent,
  MdAccessTime,
} from "react-icons/md";
import { Helmet } from 'react-helmet-async';

// Import analytics hook
import { useAnalytics } from '../hooks/useAnalytics';
import { useAffiliateCode } from '../hooks/useAffiliateCode';

const MotionBox = motion(Box);

const WebinarPage = () => {
  const { faq } = useFaq();

  // Initialize GA tracking
  const { trackEvent } = useAnalytics();

  // Initialize affiliate code tracking
  useAffiliateCode('webinar_visit');

  // Track page view
  React.useEffect(() => {
    console.log("GA Event: webinar_view");
  }, []);

  // Centralized Data from messaging.js
  const webinar = componentMessaging.webinar;
  const testimonials = componentMessaging.testimonials;

  const pageData = {
    hero: {
      badgeText: "FREE WEBINAR",
      title: webinar.title,
      subtitle: webinar.subtitle,
      duration: webinar.duration || "45-60 Minutes",
      instructor: {
        name: "Roopesh Tayaloor",
        role: "AI Expert & Industry Leader",
      },
      icons: {
        event: MdEvent,
        time: MdAccessTime,
      },
      cta: {
        label: coreMessaging.ctaFlow.webinar,
        link: "/beginner-track",
      },
    },

    learning: {
      title: "What You'll Learn",
      subtitle: "Everything you need to start your AI career journey",
      topics: webinar.keyPoints,
      icons: { check: MdCheckCircle },
    },

    registration: {
      ctaLabel: webinar.cta || 'Reserve My Spot',
      formFields: [
        {
          name: 'fullName',
          label: 'Full Name',
          type: 'text',
          required: true
        },
        {
          name: 'email',
          label: 'Email',
          type: 'email',
          required: true,
          // GA: Track form start
          onFocus: () =>
            trackEvent('webinar_registration_start', {
              page: 'webinar',
              webinar_date: '2025-10-27'
            }),
        },
        {
          name: 'phone',
          label: 'Phone (Optional)',
          type: 'tel',
          required: false
        },
      ],
      // GA: Track form submit
      onSubmitClick: () =>
        trackEvent('webinar_registration_submit', {
          page: 'webinar',
          webinar_date: '2025-10-27',
          registration_source: 'organic',
          user_type: 'student'
        }),
    },

    socialProof: {
      title: "What Past Attendees Say",
      testimonials,
    },

    nextSteps: {
      title: "What Happens Next",
      subtitle: "After attending the webinar, you'll receive:",
      steps: [
        {
          icon: MdEmail,
          title: "Step 1: Get Access",
          description: "We'll email you a curated post-webinar resource pack",
        },
        {
          icon: MdVideoLibrary,
          title: "Step 2: Learn Deeper",
          description:
            "Get guided suggestions for your first AI project and free resources",
        },
        {
          icon: MdDescription,
          title: "Step 3: Continue Learning",
          description:
            "Explore our structured programs to build real GenAI apps step by step",
        },
      ],
    },

    trust: {
      elements: [
        {
          icon: MdCheckCircle,
          title: "No Credit Card Required",
          description: "Completely free, instant access",
        },
        {
          icon: MdLockOpen,
          title: "No Obligation",
          description: "Learn at your own pace",
        },
        {
          icon: MdUnsubscribe,
          title: "Unsubscribe Anytime",
          description: "Full control of your data",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      faqs: faq.beginner.map((q) => ({
        ...q,
        // GA: Track FAQ expand
        onExpandClick: () =>
          trackEvent('faq_expand', {
            page: 'webinar',
            faq_question: q.question
          }),
      })),
    },
  };

  return (
    <>
      <Helmet>
        <title>GenAI Training | Become a Certified Full Stack AI Developer</title>
        <meta
          name="description"
          content="Launch your GenAI career with Ojasa Mirai’s mentor-led training. Build 10+ real-world AI projects, get career support, and become a certified Full Stack AI Developer in 24 weeks."
        />
      </Helmet>

      <Box>
        <Navbar />

        <MotionBox
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <WebinarHero data={pageData.hero} />

          <WebinarRegistration data={pageData.registration} />

          <WebinarLearning data={pageData.learning} />
          <WebinarSocialProof data={pageData.socialProof} />
          <WebinarNextSteps data={pageData.nextSteps} />
          <WebinarFAQ data={pageData.faq} />
          <WebinarTrust data={pageData.trust} />
        </MotionBox>

        <Footer />
      </Box>
    </>
  );
};

export default WebinarPage;
